import React, { useState, useEffect, useRef, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { Search, User, Building2, X } from 'lucide-react';
import { $api } from '../../api';
import { useOrganization } from '../../contexts/OrganizationContext';

interface Entity {
  id: string;
  name: string;
  kind: 'company' | 'person';
  email?: string;
  risk_level: 'low' | 'mid' | 'high';
  alerts_pending: number;
  created_at: string;
  updated_at: string;
}

const SearchBar: React.FC = () => {
  const navigate = useNavigate();
  const { currentOrganization } = useOrganization();
  const [searchTerm, setSearchTerm] = useState('');
  const [showResults, setShowResults] = useState(false);
  const searchRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  // Debounce search term for API calls
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState('');

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearchTerm(searchTerm);
    }, 300); // 300ms debounce

    return () => clearTimeout(timer);
  }, [searchTerm]);

  // Fetch entities from API when search term changes
  const entitiesQuery = $api.useQuery(
    'get',
    '/api/organization/{organization_id}/entities',
    {
      params: {
        path: { organization_id: currentOrganization?.id || '' },
        query: {
          search: debouncedSearchTerm || undefined,
          limit: '10', // Limit results for search dropdown
        },
      },
    },
    {
      enabled: !!currentOrganization?.id && debouncedSearchTerm.trim().length >= 2,
    }
  );

  const entities = useMemo(() => entitiesQuery.data?.entities || [], [entitiesQuery.data?.entities]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (searchRef.current && !searchRef.current.contains(event.target as Node)) {
        setShowResults(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  // Show results dropdown when there's a search term (even if no results)
  useEffect(() => {
    if (debouncedSearchTerm.trim().length >= 2) {
      // Show dropdown if searching (loading) or if we have results or if search completed with no results
      if (entitiesQuery.isLoading || entities.length > 0 || (!entitiesQuery.isLoading && entitiesQuery.isFetched)) {
        setShowResults(true);
      }
    } else {
      setShowResults(false);
    }
  }, [debouncedSearchTerm, entitiesQuery.isLoading, entitiesQuery.isFetched, entities.length]);

  const handleEntitySelect = (entity: Entity) => {
    navigate(`/${currentOrganization!.id}/entity/${entity.id}`);
    setSearchTerm('');
    setShowResults(false);
    inputRef.current?.blur();
  };

  const handleClear = () => {
    setSearchTerm('');
    setShowResults(false);
    inputRef.current?.focus();
  };

  const handleFocus = () => {
    if (debouncedSearchTerm.trim().length >= 2) {
      setShowResults(true);
    }
  };

  if (!currentOrganization) {
    return null;
  }

  return (
    <div ref={searchRef} className="relative w-full max-w-md">
      <div className="relative w-full">
        <Search className="absolute left-4 top-1/2 transform -translate-y-1/2 text-gray-400 h-5 w-5" />
        <input
          ref={inputRef}
          type="text"
          placeholder="Rechercher une entité..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          onFocus={handleFocus}
          className="w-full pl-12 pr-10 py-3 text-base border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
        />
        {searchTerm && (
          <button
            onClick={handleClear}
            className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600 transition-colors"
            type="button"
          >
            <X className="h-5 w-5" />
          </button>
        )}
      </div>

      {/* Search Results Dropdown */}
      {showResults && (
        <div className="absolute top-full right-0 mt-2 bg-white border border-gray-200 rounded-lg shadow-lg z-50 max-h-96 overflow-y-auto"
             style={{ 
               width: 'clamp(250px, 400px, calc(100vw - 2rem))',
               minWidth: '250px',
               maxWidth: 'calc(100vw - 2rem)'
             }}>
          {entitiesQuery.isLoading ? (
            <div className="p-4 text-center">
              <div className="flex items-center justify-center space-x-2">
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-600"></div>
                <span className="text-sm text-gray-500">Recherche en cours...</span>
              </div>
            </div>
          ) : entities.length === 0 ? (
            <div className="p-4 text-center text-gray-500">
              <p className="font-medium mb-1 text-gray-700">Aucune entité trouvée</p>
              <p className="text-sm">Essayez avec d'autres termes de recherche</p>
            </div>
          ) : (
            <div className="divide-y divide-gray-100">
              {entities.map((entity) => {
                const isCompany = entity.kind === 'company';
                return (
                  <button
                    key={entity.id}
                    onClick={() => handleEntitySelect(entity)}
                    className="w-full p-4 text-left hover:bg-gray-50 transition-colors flex items-center space-x-3"
                    type="button"
                  >
                    <div className="h-10 w-10 bg-blue-100 rounded-full flex items-center justify-center flex-shrink-0">
                      {isCompany ? (
                        <Building2 className="h-5 w-5 text-blue-600" />
                      ) : (
                        <User className="h-5 w-5 text-blue-600" />
                      )}
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="font-medium text-gray-900 text-base truncate">
                        {entity.name}
                      </p>
                      {entity.email && (
                        <p className="text-sm text-gray-500 truncate">{entity.email}</p>
                      )}
                    </div>
                    <div className="flex-shrink-0">
                      <span className={`px-2 py-1 text-xs font-medium rounded-full ${
                        entity.risk_level === 'low' ? 'bg-green-100 text-green-800' :
                        entity.risk_level === 'mid' ? 'bg-yellow-100 text-yellow-800' :
                        'bg-red-100 text-red-800'
                      }`}>
                        {entity.risk_level === 'low' ? 'Risque faible' :
                         entity.risk_level === 'mid' ? 'Risque moyen' : 'Risque élevé'}
                      </span>
                    </div>
                  </button>
                );
              })}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default SearchBar;

